import type { AxiosError } from 'axios'
import axios from 'axios'

const API_URL = import.meta.env.VITE_API_URL || '/api'

export const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor to add auth token and workspace header
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    // Add workspace header if available
    const workspaceId = localStorage.getItem('currentWorkspaceId')
    if (workspaceId) {
      config.headers['X-Workspace-Id'] = workspaceId
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  },
)

// Response interceptor to handle token refresh
api.interceptors.response.use(
  response => response,
  async (error: AxiosError) => {
    const originalRequest = error.config

    if (error.response?.status === 401 && originalRequest) {
      // Skip 401 handling for authentication endpoints so pages can handle errors directly
      const combinedUrl = `${originalRequest.baseURL || ''}${originalRequest.url || ''}`
      const authEndpoints = [
        '/auth/login',
        '/auth/signup',
        '/auth/refresh',
        '/auth/forgot-password',
        '/auth/reset-password',
        '/auth/complete-account-setup',
      ]
      const isAuthEndpoint = authEndpoints.some(path => combinedUrl.includes(path))
      if (isAuthEndpoint)
        return Promise.reject(error)

      try {
        const refreshToken = localStorage.getItem('refreshToken')
        if (!refreshToken)
          throw new Error('No refresh token')

        const response = await axios.post(`${API_URL}/auth/refresh`, {
          refreshToken,
        })

        const { accessToken, refreshToken: newRefreshToken } = response.data

        localStorage.setItem('accessToken', accessToken)
        localStorage.setItem('refreshToken', newRefreshToken)

        // Retry original request with new token
        // Ensure headers object exists

        originalRequest.headers = originalRequest.headers || {}

        originalRequest.headers.Authorization = `Bearer ${accessToken}`
        return api(originalRequest)
      }
      catch (refreshError) {
        // Refresh failed, redirect to login
        localStorage.removeItem('accessToken')
        localStorage.removeItem('refreshToken')
        window.location.href = '/login'
        return Promise.reject(refreshError)
      }
    }

    return Promise.reject(error)
  },
)
